import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Play, Clock, FileCheck, Calculator, DollarSign } from "lucide-react";

export const HeroSection = ({ onOpenModal }) => {
  const [spotsAvailable, setSpotsAvailable] = useState(47);

  useEffect(() => {
    // Simulate FOMO with decreasing spots
    const interval = setInterval(() => {
      setSpotsAvailable((prev) => {
        if (prev > 15 && Math.random() > 0.7) {
          return prev - 1;
        }
        return prev;
      });
    }, 15000);
    return () => clearInterval(interval);
  }, []);

  const stats = [
    { icon: Clock, value: "20 min", label: "Ahorrados por consulta" },
    { icon: FileCheck, value: "100%", label: "De la consulta documentada" },
    { icon: Calculator, value: "4", label: "Fórmulas de composición" },
    { icon: DollarSign, value: "$0", label: "Para empezar" },
  ];

  const scrollToSection = (e) => {
    e.preventDefault();
    const element = document.querySelector("#como-funciona");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center pt-20 overflow-hidden">
      {/* Background gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/0 via-black/50 to-[#050505] z-10 pointer-events-none" />
      
      <div className="relative z-20 max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-32">
        <div className="flex flex-col items-center text-center">
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-8"
          >
            <div 
              className="inline-flex items-center gap-3 bg-white/5 border border-white/10 rounded-full px-5 py-2.5 backdrop-blur-sm"
              data-testid="hero-badge"
            >
              <motion.span
                animate={{ scale: [1, 1.2, 1], opacity: [1, 0.7, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="w-2.5 h-2.5 bg-[#E05D3A] rounded-full shadow-[0_0_10px_rgba(224,93,58,0.8)]"
              />
              <span className="text-sm text-zinc-300 font-medium">
                Beta abierta — Solo <span className="text-[#E05D3A] font-bold">{spotsAvailable}</span> plazas disponibles
              </span>
            </div>
          </motion.div>

          {/* Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="font-outfit text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light tracking-tighter leading-[1.1] mb-6 max-w-5xl"
          >
            Tu consulta nutricional.{" "}
            <span className="text-gradient font-medium">Potenciada por IA.</span>
          </motion.h1>

          {/* Subheadline */}
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-base sm:text-lg md:text-xl text-zinc-400 max-w-2xl mb-10 leading-relaxed"
          >
            Antropia graba tu consulta, genera el resumen automáticamente y documenta todo por ti. 
            Más tiempo para tu cliente. Cero esfuerzo mental.
          </motion.p>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex flex-col sm:flex-row items-center gap-4 mb-16"
          >
            <button
              onClick={onOpenModal}
              className="group bg-[#E05D3A] text-white hover:bg-[#C85233] transition-all duration-300 shadow-[0_0_30px_rgba(224,93,58,0.4)] hover:shadow-[0_0_50px_rgba(224,93,58,0.6)] rounded-full px-8 py-4 text-base font-medium flex items-center gap-2"
              data-testid="hero-primary-cta"
            >
              Comenzar prueba gratuita
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              onClick={scrollToSection}
              className="group bg-white/5 text-white border border-white/10 hover:bg-white/10 transition-all duration-300 rounded-full px-8 py-4 text-base font-medium flex items-center gap-2"
              data-testid="hero-secondary-cta"
            >
              <Play className="w-4 h-4" />
              Ver cómo funciona
            </button>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 w-full max-w-4xl"
          >
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.5 + index * 0.1 }}
                className="flex flex-col items-center p-4 md:p-6 rounded-2xl bg-white/[0.02] border border-white/5 backdrop-blur-sm hover:bg-white/[0.04] transition-all"
                data-testid={`hero-stat-${index}`}
              >
                <stat.icon className="w-5 h-5 text-[#E05D3A] mb-3" />
                <span className="text-2xl md:text-3xl font-outfit font-semibold text-white mb-1">
                  {stat.value}
                </span>
                <span className="text-xs md:text-sm text-zinc-500 text-center">
                  {stat.label}
                </span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1, duration: 0.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="w-6 h-10 border-2 border-white/20 rounded-full flex justify-center pt-2"
        >
          <motion.div
            animate={{ y: [0, 12, 0], opacity: [1, 0.3, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-1.5 h-1.5 bg-[#E05D3A] rounded-full"
          />
        </motion.div>
      </motion.div>
    </section>
  );
};
